/**
 * مسارات الدخول والجلسة.
 * §5 من ملحق القرارات: الحقل اسمه **`identifier`** — بريد أو اسم مستخدم،
 * مش `email`. شاشة الدخول بتقول «البريد أو اسم المستخدم».
 */
import { api, rememberCsrf, forgetSession, ApiError } from "./client";
import type { LoginPayload, MePayload } from "./types";

export async function login(
  identifier: string,
  password: string,
  remember: boolean,
): Promise<LoginPayload> {
  const res = await api.post<LoginPayload>("/auth/login", {
    identifier,
    password,
    remember,
  });
  rememberCsrf(res.data?.csrf_token);
  return res.data;
}

export async function fetchMe(signal?: AbortSignal): Promise<MePayload> {
  const res = await api.get<MePayload>("/auth/me", { signal });
  const payload = res.data as MePayload | null | undefined;
  // ⚠️ رد ناجح بجسم فاضي = هوية مجهولة. لو عدّيناه كأنه نجاح، الشِل بيرندر
  //    بلا مستخدم و«زر تسجيل الخروج» نفسه بيختفي — الموظف بيتحبس جوّه بلا
  //    أي مخرج ولا أي رسالة. دالة ما جابتش حاجة ما تقولش «نجحت».
  if (!payload || !payload.user?.id) {
    console.error("[api] /auth/me رجّع نجاح بلا بيانات مستخدم", payload);
    throw new ApiError(
      401,
      { code: "NO_IDENTITY", message_ar: "مقدرناش نتأكد من هويتك — سجّل دخول تاني" },
      null,
    );
  }
  rememberCsrf(payload.csrf_token);
  return payload;
}

export async function logout(): Promise<void> {
  try {
    await api.post<null>("/auth/logout");
  } catch (err) {
    // ⛔ ممنوع الفشل الصامت: من غير الـcatch ده الرمية بتوقّف الخروج المحلي
    //    نفسه — الموظف بيدوس «خروج» وبيفضل داخل بلا أي رسالة.
    //    بنصرخ في اللوج وبنرمي عشان الشاشة تبلّغه إن الخروج اتم على الجهاز
    //    ده بس، والحالة المحلية بتتمسح في `finally` مهما حصل.
    console.error("[api] تسجيل الخروج ما وصلش للسيستم", err);
    throw err;
  } finally {
    // بنمسح الحالة المحلية حتى لو الطلب فشل — الكوكي بتنتهي من السيرفر
    forgetSession();
  }
}

/**
 * «نسيت كلمة السر» — بيرجّع 202 دايمًا وما بيكشفش وجود الحساب.
 *
 * 🔴 **كان بيبعت `email` والخادم بيتوقع `identifier` (اتكشف ٢٠٢٦-٠٩-١٩).**
 *    يعني كل نداء كان بيترفض بـ422 والموظف بيشوف «حصلت مشكلة مش متوقعة» —
 *    **المسار ده عمره ما اشتغل ولا مرة في عمر السيستم.**
 *
 *    ⚠️ وعاش مستخبّي لأن قناة البريد نفسها مكانش ليها مستهلك، فمحدش وصل
 *       للنقطة دي أصلًا. أول ما الإرسال اتصلح، العطل طلع في نفس اليوم.
 *
 * 🔑 والاسم `identifier` مش `email` عن قصد في الخادم: الدخول بيقبل البريد
 *    **أو اسم المستخدم**، والاستعادة بتقبل الاتنين زيه.
 */
export async function forgotPassword(identifier: string): Promise<void> {
  await api.post<null>("/auth/password/forgot", { identifier });
}

/* ══════════════════════════════════════════════════════════════════
   التسجيل الذاتي واعتماد الإدارة (docs/09-التسجيل-الذاتي-والاعتماد.md)
   ══════════════════════════════════════════════════════════════════ */

/** حالات الحساب المعتمدة — القيم من `users.status` في الباك إند */
export type AccountStatus =
  | "active"
  | "pending_email"        // سجّل ولسه ما أكّدش بريده
  | "pending_approval"     // أكّد بريده ومستني اعتماد الإدارة
  | "pending_activation"   // الاسم القديم لنفس المعنى — لسه موجود في بيانات قديمة
  | "rejected"
  | "suspended"
  | "left";

/** الحالات اللي بتوصّل شاشة «تحت المراجعة» بدل الشِل */
const PENDING_STATUSES = new Set<string>([
  "pending_email",
  "pending_approval",
  "pending_activation",
]);

export function isPendingStatus(status: string | null): boolean {
  return status !== null && PENDING_STATUSES.has(status);
}

/**
 * حالة الحساب الحالي.
 *
 * ⚠️ `CurrentUser` في `api/types.ts` لسه ما فيهوش `status` — الباك إند
 *    بيرجّعه فعلًا جوّه `/auth/me` (كل حقول المستخدم عدا الهاش). بنقرا الحقل
 *    بتضييق نوع صريح — مش `any` — لحد ما النوع نفسه يتحدّث.
 */
export function accountStatusOf(me: MePayload | null | undefined): AccountStatus | null {
  if (!me) return null;
  const raw = (me.user as { status?: unknown }).status;
  return typeof raw === "string" ? (raw as AccountStatus) : null;
}

export type RegisterInput = {
  full_name_ar: string;
  email: string;
  password: string;
  /** القسم اللي كتبه الموظف — استرشادي للمدير ومش بيدّي صلاحية */
  claimed_department: string;
};

/**
 * تسجيل ذاتي — الرد **202 دايمًا** بلا ما يكشف إن البريد مسجّل قبل كده.
 * يعني «نجاح» هنا معناه «الطلب وصل»، مش «الحساب اتعمل».
 */
export async function register(input: RegisterInput): Promise<void> {
  await api.post<null>("/auth/register", input);
}

/**
 * تغيير كلمة السر وإنت داخل.
 *
 * 🔑 السيستم بيلغي **كل الجلسات التانية** وبيسيب الحالية — يعني الجهاز اللي
 *    المدير كتب عليه الكلمة المؤقتة بيتقفل، والموظف بيفضل شغّال.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await api.post<null>("/auth/password/change", {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

/**
 * تأكيد البريد بعد التسجيل الذاتي — الرابط اللي في الإيميل.
 *
 * 🔴 **الرابط ده كان بيتبعت لصفحة مش موجودة (٢٠٢٦-٠٩-١٩).** الباك إند
 *    بيبعت `/verify-email?token=…` والواجهة مكانش فيها المسار ده خالص،
 *    فالموظفة كانت بتترمي على شاشة الدخول وتفتكر إنها غلطت، وتحاول
 *    بكلمة سر مش متسجّلة لحد ما الحساب يتقفل من المحاولات.
 *    ⚠️ والعطل عاش مستخبّي لأن الإيميل نفسه مكانش بيتبعت أصلًا — أول ما
 *       الإرسال اشتغل، العطل اللي بعده طلع على طول.
 *
 * ⛔ التأكيد **ما بيدّيش أي وصول** — بيحوّل الحساب لـ«تحت المراجعة» وبس.
 */
export type VerifyResult = { status: string; messageAr: string };

export async function verifyEmail(token: string): Promise<VerifyResult> {
  const res = await api.get<{ status?: string; message_ar?: string }>(
    `/auth/verify/${encodeURIComponent(token)}`,
  );
  const d = res.data ?? {};
  return {
    status: typeof d.status === "string" ? d.status : "pending_approval",
    messageAr:
      typeof d.message_ar === "string" && d.message_ar
        ? d.message_ar
        : "بريدك اتأكّد. طلبك تحت المراجعة — هيوصلك إشعار أول ما يتفعّل",
  };
}

/**
 * تحديد كلمة السر من رابط (دعوة · أو «نسيت كلمة السر»).
 *
 * 🔑 التوكن **استخدام واحد**، والنجاح بيلغي كل الجلسات — يعني أي جهاز
 *    كان داخل بالحساب ده بيتقفل. وده مقصود.
 */
export async function setPasswordWithToken(
  token: string,
  newPassword: string,
): Promise<void> {
  await api.post<null>("/auth/password/reset", {
    token,
    new_password: newPassword,
  });
}
